<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcProductGeneral',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    product: {
      type: Object,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const ux = ref([]);

  const $data = computed(() => ({
    ux: ux.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'ux' }],
    loaders: {},
    ux: {},
    dataRefs: { ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.product,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('product');
    }
  );

  const act_2095089031469157_emit_t0g91fqfb_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const inst_evh_2095089031469157_close_qr0po7zba_getWhen = async ($event) => {
    return !!$event.data.modalResult.value;
  };
  const act_2095089031469157_showModal_v7iy7dtkd_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'ProductEditor',
      props: {
        target: 'general',
        product: _.cloneDeep(props.product),
      },
    };
  };

  const act_7315092382398562_reloadComponentData_trqaand92 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_tzkke4itt =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_trqaand92,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_tzkke4itt],
    event: 'reload',
  });
  const act_2095089031469157_emit_t0g91fqfb = new actionTypes.EmitAction({
    actionArgs: act_2095089031469157_emit_t0g91fqfb_getActionArgs,
  });
  const inst_evh_2095089031469157_close_qr0po7zba =
    new actionTypes.EventHandlerImpl({
      action: act_2095089031469157_emit_t0g91fqfb,
      event: 'close',
      when: inst_evh_2095089031469157_close_qr0po7zba_getWhen,
      displayName: 'emit',
    });
  const act_2095089031469157_showModal_v7iy7dtkd =
    new actionTypes.ShowModalAction({
      actionArgs: act_2095089031469157_showModal_v7iy7dtkd_getActionArgs,
      events: [inst_evh_2095089031469157_close_qr0po7zba],
    });
  const inst_evh_2095089031469157_clickPreventStop_m0p0tpl08 =
    new actionTypes.EventHandlerImpl({
      action: act_2095089031469157_showModal_v7iy7dtkd,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_2095089031469157_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2095089031469157_clickPreventStop_m0p0tpl08],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_reload_tzkke4itt,
    inst_evh_7315092382398562_reload,
    inst_evh_2095089031469157_close_qr0po7zba,
    inst_evh_2095089031469157_clickPreventStop_m0p0tpl08,
    inst_evh_2095089031469157_clickPreventStop,
  };

  const evh_7315092382398562_reload_tzkke4itt = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_tzkke4itt.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_close_qr0po7zba = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_close_qr0po7zba.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_clickPreventStop_m0p0tpl08 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2095089031469157_clickPreventStop_m0p0tpl08.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpWidgetCard>
      <template #header>
        <ItpBox alignItems="center">
          <strong>
            {{ pgettext('Product', 'General') }}
          </strong>
          <ItpButton
            :text="gettext('Edit...')"
            variant="light"
            size="sm"
            class="ms-auto"
            @click.prevent.stop="
              evh_2095089031469157_clickPreventStop($event, {})
            "
          >
          </ItpButton>
        </ItpBox>
      </template>
      <BListGroup>
        <ItpLabeledListGroupItem
          :label="pgettext('Product', 'Type')"
          :content="pgettext('ProductTypes', product.product_type, 'Sentence')"
          v-if="product.product_type"
        >
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Product', 'Number')"
          :content="product.number"
        >
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Product', 'Name')"
          :content="product.name"
        >
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Product', 'Description')"
          :content="product.description"
          v-if="product.description"
        >
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Product', 'Unit')"
          v-if="product.unit"
        >
          <ItpText>
            {{ product.unit }}
          </ItpText>
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem :label="pgettext('Product', 'VAT codes')">
          <div>
            <ItpText muted>{{ pgettext('Product', 'Sales VAT') }}:</ItpText>
            {{ product.sales_vat_code }}
          </div>
          <div>
            <ItpText muted>{{ pgettext('Product', 'Purchase VAT') }}:</ItpText>
            {{ product.purchase_vat_code }}
          </div>
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Product', 'Invoice group')"
          v-if="product.invoice_group_id"
        >
          <UcInvoiceGroup :groupId="product.invoice_group_id"> </UcInvoiceGroup>
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Product', 'Intermediated service')"
          :content="gettext('Yes')"
          v-if="product.intermediated_service"
        >
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Product', 'Customs tariff no.')"
          :content="product.customs_tariff_no"
          v-if="product.customs_tariff_no"
        >
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Product', 'TESZOR code (HU)')"
          :content="product.hu_teszor_code"
          v-if="product.hu_teszor_code"
        >
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Product', 'EWC code (EU)')"
          :content="product.eu_ewc_code"
          v-if="product.eu_ewc_code"
        >
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Product', 'External key')"
          :content="product.external_key"
          v-if="product.external_key"
        >
        </ItpLabeledListGroupItem>
      </BListGroup>
    </ItpWidgetCard>
  </ItpUsercontrol>
</template>
